/**
 * 채팅방 리스트 & 메시지 로컬 캐싱 (IndexedDB)
 * - 로컬 우선 로딩으로 즉시 표시 (<100ms)
 * - 백그라운드에서 서버 동기화
 * - 카톡/인스타 방식과 유사한 Local-first Architecture
 */

const DB_NAME = 'todaydiving_chat'
const DB_VERSION = 2 // 메시지 캐싱 추가
const STORE_NAME = 'chat_rooms'
const MESSAGES_STORE_NAME = 'chat_messages'

interface CachedData<T> {
  data: T
  timestamp: number
  userId: string // 사용자별로 캐시 분리
}

class ChatCache {
  private db: IDBDatabase | null = null
  private initPromise: Promise<void> | null = null

  /**
   * IndexedDB 초기화
   */
  private async init(): Promise<void> {
    if (this.db) return
    if (this.initPromise) return this.initPromise

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        this.db = request.result
        resolve()
      }

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result

        // chat_rooms store 생성 (없으면)
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' })
          store.createIndex('userId', 'userId', { unique: false })
          store.createIndex('timestamp', 'timestamp', { unique: false })
        }

        // chat_messages store 생성 (version 2에서 추가)
        if (!db.objectStoreNames.contains(MESSAGES_STORE_NAME)) {
          const messagesStore = db.createObjectStore(MESSAGES_STORE_NAME, { keyPath: 'key' })
          messagesStore.createIndex('roomId', 'roomId', { unique: false })
          messagesStore.createIndex('timestamp', 'timestamp', { unique: false })
        }
      }
    })

    return this.initPromise
  }

  /**
   * 캐시에서 데이터 가져오기
   * @param userId 사용자 UUID
   * @returns 캐시된 채팅방 리스트 또는 null
   */
  async get<T>(userId: string): Promise<T | null> {
    try {
      await this.init()
      if (!this.db) return null

      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction([STORE_NAME], 'readonly')
        const store = transaction.objectStore(STORE_NAME)
        const request = store.get(`rooms:${userId}`)

        request.onsuccess = () => {
          const cached = request.result as CachedData<T> | undefined

          // 캐시가 없거나 다른 사용자의 캐시면 null 반환
          if (!cached || cached.userId !== userId) {
            resolve(null)
            return
          }

          // 24시간 이상 된 캐시는 삭제
          const age = Date.now() - cached.timestamp
          const MAX_AGE = 24 * 60 * 60 * 1000 // 24시간

          if (age > MAX_AGE) {
            this.delete(userId).catch(console.error)
            resolve(null)
            return
          }

          resolve(cached.data)
        }

        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.warn('[ChatCache] Failed to get from cache:', error)
      return null
    }
  }

  /**
   * 캐시에 데이터 저장
   * @param userId 사용자 UUID
   * @param data 채팅방 리스트
   */
  async set<T>(userId: string, data: T): Promise<void> {
    try {
      await this.init()
      if (!this.db) return

      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction([STORE_NAME], 'readwrite')
        const store = transaction.objectStore(STORE_NAME)

        const cached: CachedData<T> = {
          key: `rooms:${userId}`,
          data,
          timestamp: Date.now(),
          userId
        } as any

        const request = store.put(cached)

        request.onsuccess = () => resolve()
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.warn('[ChatCache] Failed to save to cache:', error)
    }
  }

  /**
   * 캐시 삭제
   * @param userId 사용자 UUID
   */
  async delete(userId: string): Promise<void> {
    try {
      await this.init()
      if (!this.db) return

      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction([STORE_NAME], 'readwrite')
        const store = transaction.objectStore(STORE_NAME)
        const request = store.delete(`rooms:${userId}`)

        request.onsuccess = () => resolve()
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.warn('[ChatCache] Failed to delete cache:', error)
    }
  }

  /**
   * 모든 캐시 삭제 (로그아웃 시)
   */
  async clear(): Promise<void> {
    try {
      await this.init()
      if (!this.db) return

      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction([STORE_NAME], 'readwrite')
        const store = transaction.objectStore(STORE_NAME)
        const request = store.clear()

        request.onsuccess = () => resolve()
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.warn('[ChatCache] Failed to clear cache:', error)
    }
  }

  /**
   * 캐시 나이 확인 (디버깅용)
   * @param userId 사용자 UUID
   * @returns 캐시된 시간 (ms) 또는 null
   */
  async getCacheAge(userId: string): Promise<number | null> {
    try {
      await this.init()
      if (!this.db) return null

      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction([STORE_NAME], 'readonly')
        const store = transaction.objectStore(STORE_NAME)
        const request = store.get(`rooms:${userId}`)

        request.onsuccess = () => {
          const cached = request.result as CachedData<any> | undefined
          if (!cached) {
            resolve(null)
            return
          }
          resolve(Date.now() - cached.timestamp)
        }

        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.warn('[ChatCache] Failed to get cache age:', error)
      return null
    }
  }

  /**
   * 메시지 캐시에서 가져오기
   * @param roomId 채팅방 ID
   * @returns 캐시된 메시지 리스트 또는 null
   */
  async getMessages(roomId: string): Promise<any[] | null> {
    try {
      await this.init()
      if (!this.db) return null

      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction([MESSAGES_STORE_NAME], 'readonly')
        const store = transaction.objectStore(MESSAGES_STORE_NAME)
        const request = store.get(`messages:${roomId}`)

        request.onsuccess = () => {
          const cached = request.result as CachedData<any[]> | undefined

          if (!cached) {
            resolve(null)
            return
          }

          // 1시간 이상 된 캐시는 삭제
          const age = Date.now() - cached.timestamp
          const MAX_AGE = 60 * 60 * 1000 // 1시간

          if (age > MAX_AGE) {
            this.deleteMessages(roomId).catch(console.error)
            resolve(null)
            return
          }

          resolve(cached.data)
        }

        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.warn('[ChatCache] Failed to get messages from cache:', error)
      return null
    }
  }

  /**
   * 메시지 캐시에 저장
   * @param roomId 채팅방 ID
   * @param messages 메시지 리스트
   */
  async setMessages(roomId: string, messages: any[]): Promise<void> {
    try {
      await this.init()
      if (!this.db) return

      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction([MESSAGES_STORE_NAME], 'readwrite')
        const store = transaction.objectStore(MESSAGES_STORE_NAME)

        const cached: CachedData<any[]> = {
          key: `messages:${roomId}`,
          data: messages,
          timestamp: Date.now(),
          roomId
        } as any

        const request = store.put(cached)

        request.onsuccess = () => resolve()
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.warn('[ChatCache] Failed to save messages to cache:', error)
    }
  }

  /**
   * 메시지 캐시 삭제
   * @param roomId 채팅방 ID
   */
  async deleteMessages(roomId: string): Promise<void> {
    try {
      await this.init()
      if (!this.db) return

      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction([MESSAGES_STORE_NAME], 'readwrite')
        const store = transaction.objectStore(MESSAGES_STORE_NAME)
        const request = store.delete(`messages:${roomId}`)

        request.onsuccess = () => resolve()
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.warn('[ChatCache] Failed to delete messages cache:', error)
    }
  }
}

// Singleton 인스턴스
export const chatCache = new ChatCache()

// Rate limiting: 요청 간 최소 간격 (ms)
const MIN_REQUEST_INTERVAL = 5000 // 5초
const CACHE_FRESH_THRESHOLD = 30000 // 30초 - 이 시간 내에는 서버 요청 안함
let lastRoomsFetchTime = 0
let roomsFetchInProgress = false

/**
 * 채팅방 리스트 fetch with cache
 *
 * 동작 순서:
 * 1. 캐시에서 즉시 로드 (있으면 바로 렌더링)
 * 2. 캐시가 충분히 신선하면 서버 요청 스킵
 * 3. 백그라운드에서 서버 동기화 (rate limiting 적용)
 *
 * @param authToken JWT 토큰
 * @param userId 사용자 UUID
 * @param onCached 캐시 데이터를 받았을 때 콜백 (즉시 렌더링용)
 * @param onFresh 서버 데이터를 받았을 때 콜백 (업데이트용)
 * @param forceRefresh 강제 새로고침 (rate limiting 무시)
 */
export async function fetchChatRooms(
  authToken: string,
  userId: string,
  onCached?: (rooms: any[]) => void,
  onFresh?: (rooms: any[]) => void,
  forceRefresh: boolean = false
): Promise<any[]> {
  // 1. 캐시에서 즉시 로드
  const cached = await chatCache.get<any[]>(userId)
  if (cached && cached.length > 0) {
    onCached?.(cached)
  }

  const now = Date.now()
  const timeSinceLastFetch = now - lastRoomsFetchTime

  // 2. Rate limiting 체크 - 중복 요청 방지
  if (roomsFetchInProgress) {
    console.log('⏳ [ChatCache] Rooms fetch already in progress, using cache')
    return cached || []
  }

  // 3. 캐시가 충분히 신선하면 서버 요청 스킵 (강제 새로고침 아닌 경우)
  if (!forceRefresh && cached && cached.length > 0 && timeSinceLastFetch < CACHE_FRESH_THRESHOLD) {
    console.log(`📦 [ChatCache] Using fresh cache (${Math.round(timeSinceLastFetch/1000)}s old)`)
    return cached
  }

  // 4. 최소 요청 간격 체크 (강제 새로고침 아닌 경우)
  if (!forceRefresh && timeSinceLastFetch < MIN_REQUEST_INTERVAL) {
    console.log(`⏱️ [ChatCache] Rate limited, using cache (${Math.round(timeSinceLastFetch/1000)}s since last fetch)`)
    return cached || []
  }

  // 5. 서버에서 최신 데이터 fetch (백그라운드)
  roomsFetchInProgress = true
  lastRoomsFetchTime = now

  try {
    const response = await fetch('/api/chat/rooms', {
      headers: { Authorization: `Bearer ${authToken}` }
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }

    const result = await response.json()

    if (result.success && result.data) {
      const rooms = result.data

      // 6. 캐시 업데이트
      await chatCache.set(userId, rooms)

      onFresh?.(rooms)

      return rooms
    } else {
      throw new Error(result.error || 'Failed to fetch rooms')
    }
  } catch (error) {
    console.error('❌ [ChatCache] Failed to fetch from server:', error)

    // 서버 실패 시 캐시된 데이터라도 반환
    if (cached) {
      console.log('⚠️ [ChatCache] Using stale cache due to server error')
      return cached
    }

    throw error
  } finally {
    roomsFetchInProgress = false
  }
}

// 메시지 rate limiting
const messagesFetchInProgress = new Map<string, boolean>()
const lastMessagesFetchTime = new Map<string, number>()

/**
 * 채팅 메시지 fetch with cache
 *
 * 동작 순서:
 * 1. 캐시에서 즉시 로드 (있으면 바로 렌더링)
 * 2. Rate limiting 및 중복 요청 체크
 * 3. 백그라운드에서 서버 동기화
 *
 * @param authToken JWT 토큰
 * @param roomId 채팅방 ID
 * @param onCached 캐시 데이터를 받았을 때 콜백 (즉시 렌더링용)
 * @param onFresh 서버 데이터를 받았을 때 콜백 (업데이트용)
 */
export async function fetchMessages(
  authToken: string,
  roomId: string,
  onCached?: (messages: any[]) => void,
  onFresh?: (messages: any[]) => void
): Promise<any[]> {
  // 1. 캐시에서 즉시 로드
  const cached = await chatCache.getMessages(roomId)
  if (cached && cached.length > 0) {
    onCached?.(cached)
  }

  const now = Date.now()
  const lastFetch = lastMessagesFetchTime.get(roomId) || 0
  const timeSinceLastFetch = now - lastFetch

  // 2. 중복 요청 방지
  if (messagesFetchInProgress.get(roomId)) {
    console.log(`⏳ [ChatCache] Messages fetch for ${roomId} already in progress`)
    return cached || []
  }

  // 3. 최소 요청 간격 체크 (3초)
  if (timeSinceLastFetch < 3000 && cached && cached.length > 0) {
    console.log(`⏱️ [ChatCache] Messages rate limited for ${roomId}`)
    return cached
  }

  // 4. 서버에서 최신 데이터 fetch
  messagesFetchInProgress.set(roomId, true)
  lastMessagesFetchTime.set(roomId, now)

  try {
    const response = await fetch(`/api/chat/messages?room_id=${roomId}&limit=20`, {
      headers: { Authorization: `Bearer ${authToken}` }
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }

    const result = await response.json()

    if (result.success && result.data?.messages) {
      const messages = result.data.messages

      // 5. 캐시 업데이트
      await chatCache.setMessages(roomId, messages)

      onFresh?.(messages)

      return messages
    } else {
      throw new Error(result.error || 'Failed to fetch messages')
    }
  } catch (error) {
    console.error('❌ [ChatCache] Failed to fetch messages from server:', error)

    // 서버 실패 시 캐시된 데이터라도 반환
    if (cached) {
      return cached
    }

    throw error
  } finally {
    messagesFetchInProgress.set(roomId, false)
  }
}
